import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Spin } from 'antd';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ApiOutlined,
  AppstoreOutlined,
  BookOutlined,
  HistoryOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { SpaceAvatar } from '../components/SpaceAvatar.tsx';
import type {
  Space,
  GlobalSearchResponse,
  GlobalSearchItemHit,
  GlobalSearchSpaceHit,
  GlobalSearchDictionaryHit,
} from '@lens/shared';
import { useAuth } from '../../features/auth/AuthContext.tsx';
import { useAiPanel } from '../../features/ai/AiPanelContext.tsx';
import { AiSparkIcon } from '../components/AiSparkIcon.tsx';
import { globalSearch } from '../../features/search/api.ts';
import styles from './CommandPalette.module.css';

/**
 * Глобальная ⌘K-командная палитра (AntD Modal — без новых зависимостей).
 *
 * Два источника результатов:
 *  1. Секция «Команды» — статичная навигация + «мои» пространства + admin +
 *     AI. Фильтруется КЛИЕНТСКИ по введённому запросу (как раньше). Гарды
 *     зеркалят серверные проверки и user-dropdown в рельсе.
 *  2. Серверный глобальный поиск (`GET /api/search`) по контенту — записи,
 *     пространства, справочники. Запускается с дебаунсом ~250мс при
 *     `q.trim().length >= 2`; прошлый запрос отменяется AbortController'ом,
 *     AbortError тихо игнорируется.
 *
 * Клавиатурная навигация (↑/↓/Enter) идёт по ПЛОСКОМУ списку всех видимых
 * строк (команды + серверные результаты) — `flatRows`.
 *
 * react-query в вебе НЕ подключён провайдером — дебаунс сделан вручную
 * через useEffect + setTimeout + AbortController (см. CLAUDE.md).
 */

interface CommandItem {
  key: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
}

/** Плоская строка для единой клавиатурной навигации. */
interface FlatRow {
  key: string;
  run: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** «Мои» пространства для быстрых переходов (mine из listSpaces). */
  spaces: Space[];
}

const DEBOUNCE_MS = 250;
// Поиск стартует с первого введённого символа. Сервер сам отдаёт пустой
// ответ на пустой запрос (контракт), а ILIKE регистронезависим (в т.ч.
// для кириллицы — локаль БД понижает регистр), так что искать можно
// сразу. Дебаунс + limit на категорию защищают от шумных однобуквенных
// запросов.
const MIN_QUERY_LEN = 1;

export function CommandPalette({
  open,
  onClose,
  spaces,
}: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const { user, canUseAi } = useAuth();
  const { togglePanel } = useAiPanel();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<InputRef>(null);

  // Сброс запроса/выделения/результатов и фокус при каждом открытии.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setResults(null);
      setLoading(false);
      // autoFocus у Input не всегда срабатывает в Modal — фокусим явно.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Дебаунс-запрос к серверному поиску. Отменяем прошлый запрос при каждом
  // изменении query; AbortError игнорируем (это намеренная отмена).
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      globalSearch(q, controller.signal)
        .then((res) => {
          setResults(res);
          setLoading(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          // Тихо гасим ошибку — палитра остаётся юзабельной по командам.
          setResults(null);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [open, query]);

  const run = (fn: () => void): void => {
    fn();
    onClose();
  };

  const commands = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      {
        key: 'nav-spaces',
        label: 'Пространства',
        icon: <AppstoreOutlined />,
        run: () => navigate('/'),
      },
      {
        key: 'nav-dictionaries',
        label: 'Справочники',
        icon: <BookOutlined />,
        run: () => navigate('/dictionaries'),
      },
      {
        key: 'nav-profile',
        label: 'Профиль',
        icon: <UserOutlined />,
        run: () => navigate('/profile'),
      },
      {
        key: 'nav-mcp-guide',
        label: 'Как подключить MCP',
        icon: <ApiOutlined />,
        run: () => navigate('/profile#mcp'),
      },
    ];

    for (const space of spaces) {
      items.push({
        key: `space-${space.id}`,
        label: space.name,
        hint: 'Открыть пространство',
        icon: <SpaceAvatar name={space.name} icon={space.icon} seed={space.id} size={20} />,
        run: () => navigate(`/s/${space.slug}`),
      });
    }

    if (user?.isPlatformAdmin) {
      items.push(
        {
          key: 'admin-users',
          label: 'Пользователи',
          hint: 'Администрирование',
          icon: <TeamOutlined />,
          run: () => navigate('/admin/users'),
        },
        {
          key: 'admin-activity',
          label: 'Журнал действий',
          hint: 'Администрирование',
          icon: <HistoryOutlined />,
          run: () => navigate('/admin/activity'),
        },
        {
          key: 'admin-settings',
          label: 'Настройки платформы',
          hint: 'Администрирование',
          icon: <SettingOutlined />,
          run: () => navigate('/admin/settings'),
        },
      );
    }

    if (canUseAi) {
      items.push({
        key: 'ai-lensy',
        label: 'Спросить Lensy',
        hint: 'AI-ассистент',
        icon: <AiSparkIcon />,
        run: () => togglePanel(),
      });
    }

    return items;
  }, [navigate, spaces, user?.isPlatformAdmin, canUseAi, togglePanel]);

  const filteredCommands = useMemo<CommandItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.hint ? c.hint.toLowerCase().includes(q) : false),
    );
  }, [commands, query]);

  // Серверные результаты показываем только когда они соответствуют текущему
  // запросу (длина >= 2). При короткой строке results уже null.
  const itemHits = results?.items ?? [];
  const spaceHits = results?.spaces ?? [];
  const dictHits = results?.dictionaries ?? [];

  const goToItem = (hit: GlobalSearchItemHit): void => {
    navigate(`/s/${hit.spaceSlug}?item=${hit.displayKey ?? hit.itemId}`);
  };
  const goToSpace = (hit: GlobalSearchSpaceHit): void => {
    navigate(`/s/${hit.slug}`);
  };
  const goToDict = (hit: GlobalSearchDictionaryHit): void => {
    navigate(`/dictionaries/${hit.id}`);
  };

  // Единый плоский список всех видимых кликабельных строк (в порядке секций):
  // команды → записи → пространства → справочники. По нему идёт ↑/↓/Enter.
  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const c of filteredCommands) rows.push({ key: c.key, run: c.run });
    for (const h of itemHits) rows.push({ key: `item-${h.itemId}`, run: () => goToItem(h) });
    for (const h of spaceHits) rows.push({ key: `sp-${h.id}`, run: () => goToSpace(h) });
    for (const h of dictHits) rows.push({ key: `dict-${h.id}`, run: () => goToDict(h) });
    return rows;
    // goToItem/goToSpace/goToDict стабильны через navigate; зависимости — данные.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCommands, itemHits, spaceHits, dictHits]);

  const flatKeyToIndex = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    flatRows.forEach((r, i) => m.set(r.key, i));
    return m;
  }, [flatRows]);

  // Держим активный индекс в границах списка.
  const safeIndex = flatRows.length === 0 ? -1 : Math.min(activeIndex, flatRows.length - 1);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (flatRows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatRows.length) % flatRows.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flatRows[safeIndex];
      if (row) run(row.run);
    }
  };

  const q = query.trim();
  const hasServerQuery = q.length >= MIN_QUERY_LEN;
  const hasAnyServer = itemHits.length > 0 || spaceHits.length > 0 || dictHits.length > 0;
  const showEmpty =
    flatRows.length === 0 && !loading && (!hasServerQuery || results !== null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnHidden
      width={720}
      styles={{ body: { padding: 0 } }}
      className={styles.modal}
      data-testid="command-palette"
    >
      <div className={styles.searchRow} onKeyDown={handleKeyDown}>
        <Input
          ref={inputRef}
          variant="borderless"
          size="large"
          placeholder="Поиск по записям, пространствам, справочникам…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          data-testid="command-palette-input"
          prefix={<span className={styles.searchPrefix}>⌘</span>}
        />
      </div>
      <div className={styles.list} role="listbox">
        {filteredCommands.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Команды</div>
            {filteredCommands.map((item) => {
              const idx = flatKeyToIndex.get(item.key) ?? -1;
              const active = idx === safeIndex;
              return (
                <button
                  type="button"
                  key={item.key}
                  role="option"
                  aria-selected={active}
                  className={`${styles.item} ${active ? styles.itemActive : ''}`}
                  onMouseEnter={() => idx >= 0 && setActiveIndex(idx)}
                  onClick={() => run(item.run)}
                >
                  <span className={styles.itemIcon}>{item.icon}</span>
                  <span className={styles.itemLabel}>{item.label}</span>
                  {item.hint ? <span className={styles.itemHint}>{item.hint}</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {itemHits.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Записи</div>
            {itemHits.map((hit) => {
              const key = `item-${hit.itemId}`;
              const idx = flatKeyToIndex.get(key) ?? -1;
              const active = idx === safeIndex;
              const primary = hit.title ?? hit.displayKey ?? 'Без названия';
              return (
                <button
                  type="button"
                  key={key}
                  role="option"
                  aria-selected={active}
                  data-testid="search-item-result"
                  className={`${styles.item} ${styles.itemResult} ${active ? styles.itemActive : ''}`}
                  onMouseEnter={() => idx >= 0 && setActiveIndex(idx)}
                  onClick={() => run(() => goToItem(hit))}
                >
                  <span className={styles.itemIcon}>
                    <AppstoreOutlined />
                  </span>
                  <span className={styles.resultBody}>
                    <span className={styles.resultPrimary}>{primary}</span>
                    {hit.snippet ? (
                      <span className={styles.resultSnippet}>{hit.snippet}</span>
                    ) : null}
                  </span>
                  <span className={styles.resultBadge}>
                    {hit.spaceName} · {hit.itemTypeName}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {spaceHits.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Пространства</div>
            {spaceHits.map((hit) => {
              const key = `sp-${hit.id}`;
              const idx = flatKeyToIndex.get(key) ?? -1;
              const active = idx === safeIndex;
              return (
                <button
                  type="button"
                  key={key}
                  role="option"
                  aria-selected={active}
                  data-testid="search-space-result"
                  className={`${styles.item} ${active ? styles.itemActive : ''}`}
                  onMouseEnter={() => idx >= 0 && setActiveIndex(idx)}
                  onClick={() => run(() => goToSpace(hit))}
                >
                  <span className={styles.itemIcon}>
                    <SpaceAvatar name={hit.name} icon={hit.icon} seed={hit.id} size={20} />
                  </span>
                  <span className={styles.itemLabel}>{hit.name}</span>
                </button>
              );
            })}
          </div>
        )}

        {dictHits.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Справочники</div>
            {dictHits.map((hit) => {
              const key = `dict-${hit.id}`;
              const idx = flatKeyToIndex.get(key) ?? -1;
              const active = idx === safeIndex;
              return (
                <button
                  type="button"
                  key={key}
                  role="option"
                  aria-selected={active}
                  data-testid="search-dictionary-result"
                  className={`${styles.item} ${active ? styles.itemActive : ''}`}
                  onMouseEnter={() => idx >= 0 && setActiveIndex(idx)}
                  onClick={() => run(() => goToDict(hit))}
                >
                  <span className={styles.itemIcon}>
                    <BookOutlined />
                  </span>
                  <span className={styles.itemLabel}>{hit.name}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading && !hasAnyServer && (
          <div className={styles.loader}>
            <Spin size="small" />
            <span>Ищем…</span>
          </div>
        )}

        {showEmpty && <div className={styles.empty}>Ничего не найдено</div>}
      </div>
    </Modal>
  );
}
